import { createContext, useState, useCallback, ReactNode } from 'react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'default'
}

interface ToastContextType {
  toasts: Toast[]
  showToast: (type: ToastType, message: string, duration?: number) => void
  removeToast: (id: string) => void
  confirm: (options: ConfirmOptions) => Promise<boolean>
  confirmState: ConfirmOptions | null
  confirmResolve: ((value: boolean) => void) | null
  closeConfirm: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const ToastContext = createContext<ToastContextType | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const [confirmState, setConfirmState] = useState<ConfirmOptions | null>(null)
  const [confirmResolve, setConfirmResolve] = useState<((value: boolean) => void) | null>(null)

  const showToast = useCallback((type: ToastType, message: string, duration = 4000) => {
    const id = Math.random().toString(36).substring(2, 9)
    const toast: Toast = { id, type, message, duration }
    
    setToasts(prev => [...prev, toast])
    
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState(options)
      setConfirmResolve(() => resolve)
    })
  }, [])

  const closeConfirm = useCallback(() => {
    setConfirmState(null)
    setConfirmResolve(null)
  }, [])

  return (
    <ToastContext.Provider value={{ 
      toasts, 
      showToast, 
      removeToast, 
      confirm, 
      confirmState, 
      confirmResolve,
      closeConfirm 
    }}>
      {children}
    </ToastContext.Provider>
  )
}
